import { Tabs, Dropdown, Button, Tooltip } from '@myua/kit';
import { ArrowDown } from '@myua/icons/dist/linear16';
import { Page, routes } from '../../router/model';
import { useHistory } from 'react-router-dom';
import { PackBadge } from '../../pack/components/PackBadge';
import { getPage } from '../../router/selectors';
import { useSelector } from 'react-redux';
import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react';
import { getPermissions } from '../selectors';
import { getCanCreateSharingLink } from '../../sharing/selectors';
import { DEMO_UNAVAILABLE_TOOLTIP, Permission } from '../../../../general/model';

type TabItem = NonNullable<ComponentProps<typeof Tabs>['items']>[number] & {
  route: string;
  tooltip?: string;
};

// Когда контейнер шапки уже этого значения, горизонтальные вкладки разделов
// начинают конфликтовать с остальными кнопками (Upload, тогл сайдбара и т.д.),
// поэтому сворачиваем их в выпадающее меню выбора раздела (MYUA-16254).
const COMPACT_WIDTH = 745;

export const PageTabs = () => {
  const history = useHistory();
  const items = useItems();
  const page = useSelector(getPage);

  // Обёртка с display: contents не влияет на раскладку (флекс-элементом шапки
  // остаётся сам Tabs/Dropdown), но даёт ref для измерения ширины контейнера.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const target = wrapRef.current?.parentElement;
    if (!target) {
      return;
    }

    const update = () => setCompact(target.clientWidth < COMPACT_WIDTH);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(target);
    return () => observer.disconnect();
    // items.length в зависимостях: вкладки появляются после асинхронной загрузки
    // прав, и обёртка с ref монтируется только тогда — нужно переподписаться.
  }, [items.length]);

  // Тултип навешиваем на label, а не на items — сам таб рендерится как div[role=tab],
  // у него нет pointer-events: none, поэтому hover ловится. В самих items label
  // остаётся простой строкой: иначе тултип протёк бы в кнопку-триггер компактного
  // режима, которая рендерит activeItem?.label.
  const tabItems = useMemo(
    () =>
      items.map(({ tooltip, label, ...item }) => ({
        ...item,
        label: tooltip ? (
          <Tooltip title={tooltip}>
            <span>{label}</span>
          </Tooltip>
        ) : (
          label
        ),
      })),
    [items],
  );

  if (!items.length) {
    return null;
  }

  const goTo = (key: string) => {
    const item = items.find((it) => it.key === key);
    if (item) {
      history.push(item.route);
    }
  };

  const activeItem = items.find((it) => it.key === page);

  // Сворачивать в меню есть смысл только при нескольких разделах.
  const showCompact = compact && items.length > 1;

  return (
    <div className="al-app-page-tabs-wrap" ref={wrapRef}>
      {showCompact ? (
        <Dropdown
          trigger={['click']}
          menu={{
            selectable: true,
            selectedKeys: page ? [page] : [],
            // disabled обязательно прокидываем: иначе через компактное меню можно
            // было бы уйти на страницу шаринга без пермишена (MYUA-16655).
            items: items.map(({ key, label, disabled, tooltip }) => ({
              key,
              disabled,
              label: tooltip ? (
                <Tooltip title={tooltip} placement="right">
                  <span>{label}</span>
                </Tooltip>
              ) : (
                label
              ),
            })),
            onClick: ({ key }) => goTo(key),
          }}
        >
          <Button className="al-app-page-tabs-compact">
            <span className="al-app-page-tabs-compact__label">
              {activeItem?.label ?? 'Sections'}
            </span>
            <ArrowDown className="al-app-page-tabs-compact__caret" />
          </Button>
        </Dropdown>
      ) : (
        <Tabs
          activeKey={page ?? undefined}
          className="al-app-page-tabs"
          size="small"
          items={tabItems}
          onChange={(activeKey) => goTo(activeKey)}
        />
      )}
    </div>
  );
};

function useItems() {
  const permissions = useSelector(getPermissions);
  const canCreateSharingLink = useSelector(getCanCreateSharingLink);

  const items = useMemo(() => {
    const result: TabItem[] = [];

    if (permissions[Permission['fs.assetlab_access']]) {
      result.push({ key: Page.viewer, label: 'Catalog', route: routes.viewer });
    }

    if (permissions[Permission['fs.assetlab_packs']]) {
      result.push({ key: Page.pack, label: <PackBadge>My pack</PackBadge>, route: routes.pack });
    }

    if (permissions[Permission['fs.assetlab_favorites']]) {
      result.push({ key: Page.favorite, label: 'Favorites', route: routes.favorite });
    }

    // На демо-стенде пермишенов шаринга нет — таб остаётся видимым, но задизейбленным
    // с демо-тултипом (MYUA-16655), по аналогии с кнопками шаринга (MYUA-16201).
    // Показываем только если есть хотя бы один доступный раздел: одинокий
    // задизейбленный таб у пользователя вообще без прав на Asset Lab смысла не имеет.
    if (result.length) {
      result.push({
        key: Page.sharingLinks,
        label: 'Shared links',
        route: routes.sharingLinks,
        disabled: !canCreateSharingLink,
        tooltip: canCreateSharingLink ? undefined : DEMO_UNAVAILABLE_TOOLTIP,
      });
    }

    return result;
  }, [permissions, canCreateSharingLink]);

  return items;
}
